import { raidQueueService, type QueuedStream } from '../lib/raidQueue';
import { TwitchStreamsService } from '../lib/twitchStreams';
import { twitchService } from '../lib/twitch';
import Swal from 'sweetalert2';
import './FloatingRaidButton.css';

declare global {
  interface Window {
    testNotification?: (streamerName: string) => void;
  }
}

export class FloatingRaidButton {
  private button: HTMLElement;
  private streamsService: TwitchStreamsService;
  private isDragging: boolean = false;
  private dragStartY: number = 0;
  private dragStartX: number = 0;
  private buttonStartY: number = 0;
  private buttonStartX: number = 0;
  private dragStartTime: number = 0;
  private lastKnownOnlineStatus: boolean = false;
  private checkInterval: number | null = null;

  constructor() {
    const accessToken = twitchService.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    this.streamsService = new TwitchStreamsService(
      import.meta.env.VITE_TWITCH_CLIENT_ID,
      accessToken
    );

    // Create wrapper div for button and waiting text
    const wrapper = document.createElement('div');
    wrapper.className = 'floating-raid-wrapper';
    
    // Create floating button
    this.button = document.createElement('button');
    this.button.className = 'floating-raid-button';
    this.button.style.display = 'none';
    this.button.innerHTML = `
      <div class="floating-raid-content">
        <img class="streamer-avatar" alt="Streamer Avatar">
        <span class="raid-icon">⚡</span>
      </div>
    `;

    // Create waiting text element
    const waitingText = document.createElement('div');
    waitingText.className = 'waiting-text';
    waitingText.style.display = 'none';
    waitingText.textContent = 'Waiting For';

    // Add elements to wrapper
    wrapper.appendChild(this.button);
    wrapper.appendChild(waitingText);
    document.body.appendChild(wrapper);
    this.initializeEventListeners();
    this.restorePosition();

    // Expose notification function to window for testing
    window.testNotification = (streamerName: string) => {
      this.showOnlineNotification(streamerName);
    };
  }

  private handleMove(clientX: number, clientY: number): void {
    // Start dragging if moved more than 5px
    if (!this.isDragging && 
        (Math.abs(clientX - this.dragStartX) > 5 || 
         Math.abs(clientY - this.dragStartY) > 5)) {
      this.isDragging = true;
      const wrapper = this.button.parentElement as HTMLElement;
      wrapper.classList.add('dragging');
    }

    if (this.isDragging) {
      const deltaX = clientX - this.dragStartX;
      const deltaY = clientY - this.dragStartY;
      const newX = this.buttonStartX + deltaX;
      const newY = this.buttonStartY + deltaY;

      // Calculate right and bottom positions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const wrapper = this.button.parentElement as HTMLElement;
      const buttonWidth = wrapper.offsetWidth;
      const buttonHeight = wrapper.offsetHeight;

      const right = viewportWidth - (newX + buttonWidth);
      const bottom = viewportHeight - (newY + buttonHeight);

      // Keep button within viewport bounds
      wrapper.style.right = `${Math.max(24, Math.min(viewportWidth - buttonWidth - 24, right))}px`;
      wrapper.style.bottom = `${Math.max(24, Math.min(viewportHeight - buttonHeight - 24, bottom))}px`;

      // Save position
      localStorage.setItem('raidButtonPosition', JSON.stringify({
        right: wrapper.style.right,
        bottom: wrapper.style.bottom
      }));
    }
  }

  private handleEnd(): void {
    if (this.isDragging) {
      const wrapper = this.button.parentElement as HTMLElement;
      wrapper.classList.remove('dragging');
    } else if (Date.now() - this.dragStartTime < 200) {
      // If not dragging and released within 200ms, treat as click
      const queue = raidQueueService.getQueue();
      if (queue.length > 0) {
        this.handleRaidClick(queue[0]);
      }
    }
    this.isDragging = false;
  }

  private initializeEventListeners(): void {
    // Mouse event handlers
    const wrapper = this.button.parentElement as HTMLElement;
    wrapper.addEventListener('mousedown', (e: MouseEvent) => {
      e.preventDefault();
      this.isDragging = false;
      this.dragStartTime = Date.now();
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      
      const rect = (this.button.parentElement as HTMLElement).getBoundingClientRect();
      this.buttonStartX = rect.left;
      this.buttonStartY = rect.top;

      const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        this.handleMove(e.clientX, e.clientY);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        this.handleEnd();
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    });

    // Touch event handlers
    wrapper.addEventListener('touchstart', (e: TouchEvent) => {
      e.preventDefault();
      this.isDragging = false;
      this.dragStartTime = Date.now();
      
      const touch = e.touches[0];
      this.dragStartX = touch.clientX;
      this.dragStartY = touch.clientY;
      
      const rect = (this.button.parentElement as HTMLElement).getBoundingClientRect();
      this.buttonStartX = rect.left;
      this.buttonStartY = rect.top;

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        const touch = e.touches[0];
        this.handleMove(touch.clientX, touch.clientY);
      };

      const handleTouchEnd = () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        this.handleEnd();
      };

      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    });

    // Start periodic status check
    this.startStatusCheck();

    // Update button when queue changes
    document.addEventListener('queueupdate', () => {
      this.updateButtonState();
    });

    // Initial update
    this.updateButtonState();
  }

  private startStatusCheck(): void {
    // Clear any existing interval
    if (this.checkInterval !== null) {
      window.clearInterval(this.checkInterval);
    }

    // Check status every 30 seconds
    this.checkInterval = window.setInterval(() => {
      this.updateButtonState();
    }, 30 * 1000);
  }

  private async updateButtonState(): Promise<void> {
    const queue = raidQueueService.getQueue();
    if (queue.length > 0) {
      const nextStream = queue[0];
      const avatar = this.button.querySelector('.streamer-avatar') as HTMLImageElement;
      avatar.src = nextStream.profileImageUrl;
      avatar.alt = nextStream.userName;
      
      const isR3dlabsEvent = nextStream.eventName !== undefined;
      const wrapper = this.button.parentElement as HTMLElement;
      const waitingText = wrapper.querySelector('.waiting-text') as HTMLElement;
      
      if (isR3dlabsEvent) {
        try {
          // Check online status for r3dlabs events
          const streamData = await this.streamsService.getStreamByUserId(nextStream.userId);
          const isOnline = streamData !== null;
          this.button.classList.toggle('disabled', !isOnline);
          waitingText.style.display = !isOnline ? 'block' : 'none';

          // Check if streamer just came online
          if (isOnline && !this.lastKnownOnlineStatus) {
            this.showOnlineNotification(nextStream.userName);
          }
          this.lastKnownOnlineStatus = isOnline;
        } catch (error) {
          // Assume offline if we can't check status
          console.error('Failed to check stream status:', error);
          this.button.classList.add('disabled');
          waitingText.style.display = 'block';
        }
      } else {
        // Regular raid queue item
        this.button.classList.remove('disabled');
        waitingText.style.display = 'none';
        this.lastKnownOnlineStatus = true; // Reset for non-r3dlabs events
      }
      
      this.button.style.display = 'flex';
      wrapper.style.display = 'flex';
    } else {
      const wrapper = this.button.parentElement as HTMLElement;
      wrapper.style.display = 'none';
      // Stop checking status when queue is empty
      if (this.checkInterval !== null) {
        window.clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    }
  }

  private restorePosition(): void {
    const savedPosition = localStorage.getItem('raidButtonPosition');
    if (savedPosition) {
      try {
        const position = JSON.parse(savedPosition);
        const wrapper = this.button.parentElement as HTMLElement;
        wrapper.style.right = position.right;
        wrapper.style.bottom = position.bottom;
      } catch (error) {
        console.error('Failed to restore button position:', error);
      }
    }
  }

  // Make public for testing via console
  public showOnlineNotification(streamerName: string): void {
    const notification = document.createElement('div');
    notification.className = 'online-notification';
    notification.innerHTML = `
      <span class="icon">⚡</span>
      <span><span class="streamer-name">${streamerName}</span> is now online!</span>
      <span class="icon">⚡</span>
    `;
    document.body.appendChild(notification);

    // Remove notification after animation completes
    notification.addEventListener('animationend', () => {
      document.body.removeChild(notification);
    });
  }

  private async handleRaidClick(stream: QueuedStream): Promise<void> {
    // For r3dlabs events, check if online first
    if (stream.eventName !== undefined) {
      try {
        const streamStatus = await this.streamsService.getStreamByUserId(stream.userId);
        if (!streamStatus) {
          // Stream is offline, do nothing
          return;
        }
      } catch (error) {
        console.error('Failed to check stream status:', error);
        return;
      }
    }
    const result = await Swal.fire({
      title: 'Start Raid?',
      html: `Are you sure you want to raid <strong>${stream.userName}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Start Raid!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#9147ff',
      cancelButtonColor: '#6e7681',
      background: '#18181b',
      color: '#ffffff',
      width: '360px'
    });

    if (result.isConfirmed) {
      try {
        this.button.classList.add('loading');
        await this.streamsService.raidChannel(stream.userId);
        
        await Swal.fire({
          title: 'Raid Started!',
          html: `Successfully started raid to <strong>${stream.userName}</strong>`,
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          confirmButtonColor: '#9147ff',
          background: '#18181b',
          color: '#ffffff',
          width: '360px'
        });

        // Remove the raided stream from queue
        raidQueueService.removeFromQueue(stream.userId);
      } catch (error) {
        console.error('Failed to start raid:', error);
        
        const errorMessage = error instanceof Error ? error.message : 'Unable to start the raid. Please try again.';
        await Swal.fire({
          title: 'Unable to Start Raid',
          html: `<div style="text-align: left; margin-top: 1rem;">${errorMessage}</div>`,
          icon: 'error',
          confirmButtonColor: '#9147ff',
          background: '#18181b',
          color: '#ffffff',
          width: '360px'
        });
      } finally {
        this.button.classList.remove('loading');
      }
    }
  }
}
